/* eslint-disable @typescript-eslint/naming-convention */
import { useContext, useState } from "react";
import styled from "styled-components";
import { Button, Modal } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import {
  ArrowTopRightOnSquareIcon,
  CheckmarkSolidIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  PencilSquareIcon,
  TrashIcon,
  XCircleIcon
} from "@dealer-center/core-components";
import { useOutletContext } from "react-router-dom";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";
import type { decodedJwtAndPermissions } from "@dealer-center/ddc-api-user-interface";
import type { IncentivesRecord } from "@dealer-center/ddc-pages-incentives-manager-interface";
import { FiltersContext } from "../providers/FiltersProvider";
import { cloneIncentive, getSelectedIds } from "../utils/utils";
import { getButtonDisabledState } from "../utils/tabActionPanelUtils";
import { useIncentiveActions } from "../hooks/useIncentiveActions";
import "./TabActionPanel.scss";

interface ActionData {
  currentTab: string;
  selections: number[];
  incentives?: IncentivesRecord[];
  showIncentivePopup?: (
    isShowing: boolean,
    type: string,
    incentive?: IncentivesRecord
  ) => void;
}

const ActionPanel = styled.div`
  padding: 12px 0px;
  display: flex;
  gap: 6px;
  margin-left: 14px;
`;

const EDIT_BUTTON = {
  label: "EDIT",
  icon: <PencilSquareIcon title={Locale.getText("EDIT_ICON") as string} />,
  noBulk: true,
  message: ""
};

const CLONE_BUTTON = {
  label: "CLONE_INCENTIVE",
  icon: (
    <DocumentDuplicateIcon title={Locale.getText("CLONE_ICON") as string} />
  ),
  noBulk: true,
  message: ""
};

const PUBLISH_BUTTON = {
  label: "PUBLISH",
  icon: (
    <ArrowTopRightOnSquareIcon
      title={Locale.getText("PUBLISH_ICON") as string}
    />
  ),
  noBulk: false,
  message: "PUBLISH_ALERT"
};

const DELETE_BUTTON = {
  label: "DELETE",
  icon: <TrashIcon title={Locale.getText("DELETE_ICON") as string} />,
  noBulk: false,
  message: "DELETE_ALERT_WITH_COUNT"
};

const buttonData = {
  DRAFTS: [EDIT_BUTTON, CLONE_BUTTON, PUBLISH_BUTTON, DELETE_BUTTON],
  ACTION_REQUIRED: [
    EDIT_BUTTON,
    {
      label: "APPROVE",
      icon: (
        <CheckmarkSolidIcon title={Locale.getText("APPROVE_ICON") as string} />
      ),
      noBulk: false,
      message: "APPROVE_ALERT"
    },
    CLONE_BUTTON,
    {
      label: "REJECT",
      icon: <XCircleIcon title={Locale.getText("REJECT_ICON") as string} />,
      noBulk: false,
      message: "REJECT_ALERT"
    }
  ],
  UNDER_COMPLIANCE_REVIEW: [EDIT_BUTTON, CLONE_BUTTON, DELETE_BUTTON],
  PENDING_PUBLICATION: [EDIT_BUTTON, CLONE_BUTTON, DELETE_BUTTON],
  PUBLISHED: [EDIT_BUTTON, CLONE_BUTTON, DELETE_BUTTON],
  DECLINED: [CLONE_BUTTON, PUBLISH_BUTTON],
  EXPIRED: [
    {
      label: "OPEN",
      icon: <FolderIcon title={Locale.getText("OPEN_ICON") as string} />,
      noBulk: true,
      message: ""
    },
    CLONE_BUTTON
  ]
};

export const TabActionPanel = ({
  currentTab,
  selections,
  incentives,
  showIncentivePopup
}: ActionData) => {
  const { dispatch, isMakeLocked } = useContext(FiltersContext);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [modalButton, setModalButton] = useState<{
    label: string;
    message: string;
  } | null>(null);
  const { ccUserInfo } = useOutletContext<{
    ccUserInfo: ControlcenterAPIInterfaceExternal;
    userEntity: decodedJwtAndPermissions;
  }>();

  const { onChangeStatus, onDeleteStatus, onPublish, onApprove } =
    useIncentiveActions({
      ccUserInfo,
      currentTab,
      dispatch,
      setIsChanging,
      setIsDeleting,
      setModalButton,
      incentives
    });

  const handleButton = async (button: any) => {
    let status = "";
    switch (button.label) {
      case "APPROVE":
        setIsChanging(true);
        await onApprove(getSelectedIds(selections, incentives));
        break;
      case "PUBLISH":
        setIsChanging(true);
        await onPublish(getSelectedIds(selections, incentives));
        break;
      case "REJECT":
        status = "REJECTED";
        break;
      case "DELETE":
        status = "DELETE";
        setIsDeleting(true);
        break;
      case "EDIT":
        status = "EDIT";
        break;
      case "CLONE_INCENTIVE":
        if (showIncentivePopup && incentives && selections.length > 0) {
          const clonedIncentive = cloneIncentive(
            incentives?.[selections[0]],
            ccUserInfo.accountId
          );
          showIncentivePopup(true, "CLONE_INCENTIVE", clonedIncentive);
        }
        break;
      case "OPEN":
        showIncentivePopup?.(
          true,
          "OPEN_INCENTIVE",
          incentives?.[selections[0]]
        );
        break;
      default:
        break;
    }
    if (status) {
      if (status === "DELETE") {
        onDeleteStatus(getSelectedIds(selections, incentives));
      } else if (status === "EDIT") {
        const incentive = incentives?.find(
          incentive =>
            incentive.content.incentiveId ===
            incentives[selections[0]].content.incentiveId
        );
        if (incentive && showIncentivePopup) {
          showIncentivePopup(true, "EDIT_INCENTIVE", incentive);
        }
      } else {
        onChangeStatus(getSelectedIds(selections, incentives), status);
      }
    }
  };

  const buildButtons = (currentTab: string) => {
    if (buttonData[currentTab]) {
      return buttonData[currentTab].map(button => (
        <Button
          analyticsConfiguration={{
            eventName: "",
            eventProperties: undefined
          }}
          buttonStyle="secondary"
          disabled={getButtonDisabledState(
            button,
            selections,
            incentives,
            isMakeLocked,
            ccUserInfo?.userPermissions
          )}
          key={`${currentTab}-${button.label}`}
          startIcon={button.icon}
          onClick={() => {
            if (button.message) {
              setModalButton(button);
            } else {
              handleButton(button);
            }
          }}
        >
          {Locale.getText(button.label)}
        </Button>
      ));
    }
    return null;
  };

  return (
    <>
      <Modal
        footer={{
          primary: {
            label: Locale.getText("APPLY") as string,
            action: () => {
              handleButton(modalButton);
            },
            isLoading: isDeleting || isChanging
          },
          options: [
            {
              buttonStyle: "secondary",
              label: Locale.getText("CANCEL") as string,
              action: () => setModalButton(null)
            }
          ]
        }}
        show={!!modalButton}
        size="small"
      >
        {modalButton?.label === "DELETE" ? (
          <p style={{ paddingLeft: "16px" }}>
            {(Locale.getText("DELETE_ALERT_WITH_COUNT") as string).replace(
              "{count}",
              selections.length.toString()
            )}
          </p>
        ) : (
          <p style={{ paddingLeft: "16px" }}>
            {Locale.getText(modalButton?.message || "")}
          </p>
        )}
      </Modal>
      <ActionPanel>{buildButtons(currentTab)}</ActionPanel>
    </>
  );
};
